import { useState, useEffect } from "react";
import { useForm, Controller } from 'react-hook-form';
import { useDempStore } from "src/context/stores";
import { createOperation, updateOperation } from "../../services/useEsgApi";

const AddOperationPopup = ({
  isOpen,
  onClose,
  groupYear,
  editItem = null,
  setSelectedYear,
  onOperationSubmit
}) => {
  const companyId = useDempStore((state) => state.companyId);
  const [error, setError] = useState(null);
  
  const { control, handleSubmit, formState: { errors }, reset } = useForm({
    defaultValues: editItem || {
      policy: {
        companyPotentialOperation: '',
        industrialOperation: '',
        companyOperation: ''
      },
      disclosure: {
        companyPotentialOperation: '',
        industrialOperation: '',
        companyOperation: ''
      },
      target: {
        companyPotentialOperation: '',
        industrialOperation: '',
        companyOperation: ''
      },
      performance: {
        companyPotentialOperation: '',
        industrialOperation: '',
        companyOperation: ''
      },
      implementation: {
        companyPotentialOperation: '',
        industrialOperation: '',
        companyOperation: ''
      }
    }
  });

  useEffect(() => {
    if (editItem) {
      reset(editItem);
    }
  }, [editItem, reset]);

  const onSubmit = async (data) => {
    setError(null);
    
    try {
      const processedData = {};
      Object.entries(data).forEach(([section, fields]) => {
        processedData[section] = {};
        Object.entries(fields).forEach(([field, value]) => {
          if (field === 'companyPotentialOperation' && groupYear <= 2023) {
            return;
          }
          processedData[section][field] = value ? parseInt(value) : '';
        });
      });

      const response = editItem 
        ? await updateOperation(processedData, companyId, groupYear)
        : await createOperation(processedData, companyId, groupYear);
      
      if (response.error) {
        throw new Error(response.error);
      }

      if (onOperationSubmit) {
        onOperationSubmit(groupYear, processedData);
      }
      
      setSelectedYear(prev => prev.filter(y => y !== groupYear));
      setTimeout(() => setSelectedYear(prev => [...prev, groupYear].sort((a, b) => a - b)), 100);
      onClose();
    } catch (err) {
      setError(err.message || "An unexpected error occurred. Please try again.");
      console.error("Error processing operation:", err);
    }
  };

  if (!isOpen) return null;

  const overviewQuestion = [
    { id: 'policy', title: 'Policy' },
    { id: 'disclosure', title: 'Disclosure' },
    { id: 'target', title: 'Target' },
    { id: 'performance', title: 'Performance' },
    { id: 'implementation', title: 'Implementation' }
  ];

  const fieldQuestion = [
    ...(groupYear >= 2024 ? [{ id: 'companyPotentialOperation', label: "Company's Potential Operation" }] : []),
    { id: 'industrialOperation', label: "Industrial Operation" },
    { id: 'companyOperation', label: "Company's Operation" }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#79C8B0] to-[#1FA37C] flex items-center justify-center">
                <span className="text-2xl font-bold text-white">+</span>
              </div>
              <div>
                <h3 className="text-xl font-bold">{groupYear} | {editItem ? 'Edit Operation' : 'Add Operation'}</h3>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} id="operationForm" className="flex-1 overflow-y-auto">
          <section className="bg-white rounded-xl border p-6 space-y-6">
            <div className="flex items-center justify-between">
              <h4 className="text-lg font-semibold text-gray-800">
                Overview of the company's operation
              </h4>
            </div>

            <div className="space-y-8">
              {overviewQuestion.map(section => (
                <div key={section.id}>
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">{section.title}</h4>
                  <div className={`grid ${groupYear >= 2024 ? 'grid-cols-3' : 'grid-cols-2'} gap-6 mt-4 pl-4`}>
                    {fieldQuestion.map(field => (
                      <div key={field.id} className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">{field.label}</label>
                        <Controller
                          name={`${section.id}.${field.id}`}
                          control={control}
                          render={({ field: controllerField }) => (
                            <select 
                              {...controllerField} 
                              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-200 transition-all"
                            >
                              <option value="">Select rating (1-10)</option>
                              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                                <option key={num} value={num}>{num}</option>
                              ))}
                            </select>
                          )}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {error && (
              <div className="p-3 text-sm text-red-700 bg-red-100 rounded-md">
                {error}
              </div>
            )}
          </section>
        </form>

        <div className="border-t p-6 flex justify-end gap-4">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-gradient-to-r from-red-500 to-red-700 text-white rounded-lg hover:opacity-90 transition-all font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="operationForm"
            className="px-6 py-2.5 bg-gradient-to-r from-[#79C8B0] to-[#1FA37C] text-white rounded-lg hover:opacity-90 transition-all font-medium"
          >
            {editItem ? "Update" : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddOperationPopup;